import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import type { TeamMemberWithPlanning, DayOfWeek } from '../types/team.types';
import { DAY_LABELS } from '../types/team.types';
import './TeamCalendar.css';

interface WeekDate {
  date: string;
  dayOfWeek: string;
  dayNumber: number;
}

interface TeamCalendarProps {
  teamPlanning: TeamMemberWithPlanning[];
  weekDates: WeekDate[];
  currentWeekStart: string;
}

// Time range for the calendar (6 AM to 23:30 PM)
const START_HOUR = 6;
const END_HOUR = 24; // Use 24 to represent end of day (23:30 rounds up)
const HOURS_RANGE = END_HOUR - START_HOUR;

// Generate hour labels (6h to 23h)
const HOURS = Array.from({ length: HOURS_RANGE }, (_, i) => START_HOUR + i);

// Colors for team members
const TEAM_COLOR = '#3B82F6'; // blue (default)
const AVAILABLE_NOW_COLOR = '#10B981'; // green (currently available)

// Calculate position and width for a time slot
const getSlotStyle = (start: string, end: string): { left: string; width: string } => {
  const [startHour, startMin] = start.split(':').map(Number);
  const [endHour, endMin] = end.split(':').map(Number);

  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;
  const rangeStart = START_HOUR * 60;
  const rangeEnd = END_HOUR * 60;
  const totalMinutes = rangeEnd - rangeStart;

  // Clamp to visible range
  const visibleStart = Math.max(startMinutes, rangeStart);
  const visibleEnd = Math.min(endMinutes, rangeEnd);

  if (visibleEnd <= visibleStart) {
    return { left: '0%', width: '0%' };
  }

  const left = ((visibleStart - rangeStart) / totalMinutes) * 100;
  const width = ((visibleEnd - visibleStart) / totalMinutes) * 100;

  return {
    left: `${left}%`,
    width: `${width}%`
  };
};

// Get initials from name
const getInitials = (firstName: string, lastName: string): string => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

export const TeamCalendar: React.FC<TeamCalendarProps> = ({
  teamPlanning,
  weekDates,
  currentWeekStart
}) => {
  // Check if a date is today
  const today = useMemo(() => {
    const now = new Date();
    return format(now, 'yyyy-MM-dd');
  }, []);

  // Get current time for the "now" indicator
  const currentTime = useMemo(() => {
    const now = new Date();
    return {
      hour: now.getHours(),
      minute: now.getMinutes()
    };
  }, []);

  // Calculate "now" indicator position
  const getNowIndicatorPosition = (): string | null => {
    const minutes = currentTime.hour * 60 + currentTime.minute;
    const rangeStart = START_HOUR * 60;
    const rangeEnd = END_HOUR * 60;

    if (minutes < rangeStart || minutes > rangeEnd) {
      return null;
    }

    const totalMinutes = rangeEnd - rangeStart;
    const position = ((minutes - rangeStart) / totalMinutes) * 100;
    return `${position}%`;
  };

  // Check if any team member has appointments blocking their availability
  const getAppointmentsForDay = (member: TeamMemberWithPlanning, date: string) => {
    return member.appointments.filter(apt => apt.date === date);
  };

  // Check if a user is currently available (today + within working hours + no blocking appointment)
  const isAvailableNow = (member: TeamMemberWithPlanning, date: string, dayKey: DayOfWeek): boolean => {
    // Must be today
    if (date !== today) return false;

    const dayPlanning = member.planning[dayKey];
    if (!dayPlanning?.enabled) return false;

    // Check if current time is within working hours
    const currentMinutes = currentTime.hour * 60 + currentTime.minute;
    const [startH, startM] = dayPlanning.start.split(':').map(Number);
    const [endH, endM] = dayPlanning.end.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    if (currentMinutes < startMinutes || currentMinutes > endMinutes) {
      return false;
    }

    // Check if there's a blocking appointment right now
    const appointments = getAppointmentsForDay(member, date);
    const hasBlockingAppointment = appointments.some((apt) => {
      const [aptStartH, aptStartM] = apt.startTime.split(':').map(Number);
      const [aptEndH, aptEndM] = apt.endTime.split(':').map(Number);
      const aptStart = aptStartH * 60 + aptStartM;
      const aptEnd = aptEndH * 60 + aptEndM;
      return currentMinutes >= aptStart && currentMinutes <= aptEnd;
    });

    return !hasBlockingAppointment;
  };

  // State to show/hide past days
  const [showPastDays, setShowPastDays] = useState(false);

  // State for view mode: 'week' or 'day'
  const [viewMode, setViewMode] = useState<'week' | 'day'>('day');

  // State for selected day index (0-6 for Mon-Sun) in day view
  const [selectedDayIndex, setSelectedDayIndex] = useState<number>(() => {
    // Default to today
    const now = new Date();
    const dayOfWeek = now.getDay();
    // getDay(): 0=Sun, 1=Mon ... 6=Sat → convert to Mon=0 ... Sun=6
    return dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  });

  // Get today's date for local comparison
  const todayLocal = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // All days (Monday-Sunday)
  const allWorkDays = useMemo(() => weekDates, [weekDates]);

  // Only show workdays by default (Monday-Friday), and optionally hide past days
  const workDays = useMemo(() => {
    if (viewMode === 'day') {
      // In day view, show only the selected day
      const selectedDay = allWorkDays[selectedDayIndex];
      return selectedDay ? [selectedDay] : [];
    }
    // In week view, filter past days if needed
    if (showPastDays) {
      return allWorkDays;
    }
    return allWorkDays.filter(day => day.date >= todayLocal);
  }, [allWorkDays, viewMode, selectedDayIndex, showPastDays, todayLocal]);

  // Check if there are past days that are hidden
  const hasPastDays = useMemo(() => {
    return allWorkDays.some(day => day.date < todayLocal);
  }, [allWorkDays, todayLocal]);

  // Navigation for day view
  const canGoPrevDay = selectedDayIndex > 0;
  const canGoNextDay = selectedDayIndex < 6;
  const goToPrevDay = () => setSelectedDayIndex(prev => Math.max(0, prev - 1));
  const goToNextDay = () => setSelectedDayIndex(prev => Math.min(6, prev + 1));

  return (
    <div className={`team-calendar ${viewMode === 'day' ? 'day-view' : 'week-view'}`}>
      {/* View controls */}
      <div className="team-calendar-controls">
        {/* View mode toggle */}
        <div className="view-mode-toggle">
          <button
            className={`view-mode-button ${viewMode === 'day' ? 'active' : ''}`}
            onClick={() => setViewMode('day')}
          >
            Jour
          </button>
          <button
            className={`view-mode-button ${viewMode === 'week' ? 'active' : ''}`}
            onClick={() => setViewMode('week')}
          >
            Semaine
          </button>
        </div>

        {/* Day navigation (only in day view) */}
        {viewMode === 'day' && (
          <div className="day-navigation">
            <button
              className="day-nav-button"
              onClick={goToPrevDay}
              disabled={!canGoPrevDay}
            >
              ←
            </button>
            <div className="day-selector">
              {allWorkDays.map((day, index) => (
                <button
                  key={day.date}
                  className={`day-select-button ${index === selectedDayIndex ? 'active' : ''} ${day.date === todayLocal ? 'is-today' : ''}`}
                  onClick={() => setSelectedDayIndex(index)}
                >
                  {DAY_LABELS[day.dayOfWeek as DayOfWeek].short}
                </button>
              ))}
            </div>
            <button
              className="day-nav-button"
              onClick={goToNextDay}
              disabled={!canGoNextDay}
            >
              →
            </button>
          </div>
        )}

        {/* Toggle past days (only in week view) */}
        {viewMode === 'week' && hasPastDays && (
          <button
            className={`toggle-past-button ${showPastDays ? 'active' : ''}`}
            onClick={() => setShowPastDays(!showPastDays)}
          >
            {showPastDays ? 'Masquer les jours passés' : 'Afficher les jours passés'}
          </button>
        )}
      </div>

      {/* Time header */}
      <div className="team-calendar-header">
        <div className="team-calendar-day-label">Jour</div>
        <div className="team-calendar-timeline-header">
          {HOURS.map((hour) => (
            <div key={hour} className="team-calendar-hour-label">
              {hour}h
            </div>
          ))}
        </div>
      </div>

      {/* Days rows */}
      <div className="team-calendar-body">
        {workDays.map((weekDate) => {
          const dayKey = weekDate.dayOfWeek as DayOfWeek;
          const isToday = weekDate.date === today;
          const nowPosition = isToday ? getNowIndicatorPosition() : null;

          return (
            <div
              key={weekDate.date}
              className={`team-calendar-day-row ${isToday ? 'is-today' : ''}`}
            >
              {/* Day label */}
              <div className="team-calendar-day-label">
                <span className="day-name">{DAY_LABELS[dayKey].short}</span>
                <span className={`day-number ${isToday ? 'today' : ''}`}>
                  {weekDate.dayNumber}
                </span>
              </div>

              {/* Timeline for this day */}
              <div className="team-calendar-timeline">
                {/* Hour grid lines */}
                <div className="team-calendar-grid">
                  {HOURS.map((hour) => (
                    <div key={hour} className="team-calendar-grid-line" />
                  ))}
                </div>

                {/* Now indicator */}
                {nowPosition && (
                  <div
                    className="team-calendar-now-indicator"
                    style={{ left: nowPosition }}
                  />
                )}

                {/* Team members availability blocks */}
                <div className="team-calendar-members">
                  {/* Sort: by starting time, then office first */}
                  {[...teamPlanning]
                    .filter((member) => member.planning[dayKey]?.enabled)
                    .sort((a, b) => {
                      // First sort by starting time
                      const aStart = a.planning[dayKey]?.start || '09:00';
                      const bStart = b.planning[dayKey]?.start || '09:00';
                      const [aH, aM] = aStart.split(':').map(Number);
                      const [bH, bM] = bStart.split(':').map(Number);
                      const aMinutes = aH * 60 + aM;
                      const bMinutes = bH * 60 + bM;
                      if (aMinutes !== bMinutes) return aMinutes - bMinutes;

                      // Then sort by location (office first)
                      const aLocation = a.planning[dayKey]?.workLocation || 'office';
                      const bLocation = b.planning[dayKey]?.workLocation || 'office';
                      if (aLocation === 'office' && bLocation === 'remote') return -1;
                      if (aLocation === 'remote' && bLocation === 'office') return 1;
                      return 0;
                    })
                    .map((member) => {
                    const dayPlanning = member.planning[dayKey];
                    const appointments = getAppointmentsForDay(member, weekDate.date);
                    const availableNow = isAvailableNow(member, weekDate.date, dayKey);
                    const color = availableNow ? AVAILABLE_NOW_COLOR : TEAM_COLOR;

                    const slotStyle = getSlotStyle(dayPlanning.start, dayPlanning.end);

                    return (
                      <div
                        key={member.user.id}
                        className="team-member-row"
                      >
                        {/* Main availability block */}
                        <div
                          className={`team-member-availability ${availableNow ? 'available-now' : ''}`}
                          style={{
                            left: slotStyle.left,
                            width: slotStyle.width,
                            backgroundColor: `${color}20`,
                            borderColor: color
                          }}
                          title={`${member.user.firstName} ${member.user.lastName} - ${dayPlanning.start} à ${dayPlanning.end}${dayPlanning.workLocation === 'remote' ? ' (Télétravail)' : ''}${availableNow ? ' (Disponible maintenant)' : ''}`}
                        >
                          <div
                            className="team-member-avatar"
                            style={{ backgroundColor: color }}
                          >
                            {getInitials(member.user.firstName, member.user.lastName)}
                          </div>
                          <span className="team-member-name">
                            {member.user.firstName}
                          </span>
                          <span className="team-member-hours">
                            {dayPlanning.workLocation === 'remote' && (
                              <span className="team-member-remote-badge" title="Télétravail">
                                🏠
                              </span>
                            )}
                            {dayPlanning.start} → {dayPlanning.end}
                          </span>
                        </div>

                        {/* Appointment overlays (blocking) */}
                        {appointments.map((apt) => {
                          const aptStyle = getSlotStyle(apt.startTime, apt.endTime);
                          const isPro = apt.isProfessional;
                          return (
                            <div
                              key={apt.id}
                              className={`team-member-appointment ${isPro ? 'professional' : ''}`}
                              style={{
                                left: aptStyle.left,
                                width: aptStyle.width
                              }}
                              title={`${member.user.firstName}: ${apt.title} (${apt.startTime} - ${apt.endTime})${isPro ? ' - Professionnel' : ''}`}
                            >
                              <span className="appointment-stripe" />
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="team-calendar-legend">
        <div className="legend-item">
          <span className="legend-dot available-now" />
          <span>Disponible maintenant</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot office" />
          <span>Disponible</span>
        </div>
        <div className="legend-item">
          <span>🏠</span>
          <span>Télétravail</span>
        </div>
        <div className="legend-item">
          <span className="legend-stripe" />
          <span>Indisponible (personnel)</span>
        </div>
        <div className="legend-item">
          <span className="legend-stripe professional" />
          <span>Professionnel (réunion, etc.)</span>
        </div>
      </div>
    </div>
  );
};

export default TeamCalendar;
